import SolutionLayout from "@/pages/solutions/components/SolutionLayout";

const features = [
  {
    title: "CLI validation engine",
    desc: "Every outbound call is checked against a real-time CLI whitelist. Spoofed, manipulated, or invalid CLIs are rejected before the call leaves our SBC.",
    icon: "ri-fingerprint-line",
  },
  {
    title: "Velocity-based fraud detection",
    desc: "Monitor call patterns per destination, per account, per IP. Sudden spikes, round-robin dialling, and unusual answer ratios trigger automatic alerts.",
    icon: "ri-speed-line",
  },
  {
    title: "Revenue-share fraud blocking",
    desc: "Blacklists of known revenue-fraud destinations are updated daily. Calls to high-risk ranges are blocked at the routing layer, not billed.",
    icon: "ri-shield-check-line",
  },
  {
    title: "ASR threshold routing",
    desc: "If a destination's ASR collapses below expected norms — a classic fraud or SIM-box indicator — that route is automatically removed from LCR.",
    icon: "ri-bar-chart-grouped-line",
  },
  {
    title: "Account-level rate limiting",
    desc: "Set per-account CPS caps, daily spend limits, and burst-rate throttles. Great for protecting against hijacked credentials or internal misconfigurations.",
    icon: "ri-lock-2-line",
  },
  {
    title: "24/7 fraud NOC",
    desc: "A dedicated anti-fraud team monitors global abuse patterns around the clock. When they see a new threat, they push protective rules fleet-wide.",
    icon: "ri-customer-service-2-line",
  },
];

const stats = [
  { value: "<0.1%", label: "Fraud rate across all traffic" },
  { value: "24/7", label: "Dedicated fraud NOC" },
  { value: "100%", label: "CLI validation coverage" },
  { value: "5min", label: "Threat response time" },
];

export default function AntiFraud() {
  return (
    <SolutionLayout
      title="Anti-fraud & Quality Control"
      tagline="CLI validation, velocity-based fraud detection, and real-time route monitoring — built into every call that leaves our network."
      badge="Security · Anti-fraud"
      breadcrumbLabel="Anti-fraud"
    >
      <section className="pb-10 md:pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
                <div className="text-2xl md:text-3xl font-semibold font-heading text-foreground-950">{s.value}</div>
                <div className="text-xs md:text-sm text-foreground-600 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-10">
            <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">Fraud protection</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
              We catch fraud before it becomes your problem.
            </h2>
            <p className="mt-3 text-foreground-700 max-w-2xl">
              Fraud costs carriers millions. Our multi-layer system validates every call, monitors every destination, and acts on threats in minutes — not days.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => (
              <div key={f.title} className="rounded-xl border border-background-200 bg-background-50 p-6 hover:border-primary-400 transition-colors">
                <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className={`${f.icon} text-xl w-6 h-6 flex items-center justify-center`} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950 font-heading">{f.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SolutionLayout>
  );
}